import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logActivity } from '@/hooks/useActivityLog';
import { DuplicateCandidate } from '@/components/DuplicateCheckDialog';

interface CheckParams {
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
}

export const useDuplicateCheck = () => {
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const checkForDuplicates = useCallback(async (params: CheckParams): Promise<boolean> => {
    const { firstName, lastName, dateOfBirth } = params;

    if (!firstName.trim() && !lastName.trim()) return false;

    setIsChecking(true);
    try {
      const conditions: string[] = [];
      const fnWild = `%${firstName.trim()}%`;
      const lnWild = `%${lastName.trim()}%`;

      // Name match
      if (firstName.trim() && lastName.trim()) {
        conditions.push(`and(first_name.ilike.${fnWild},last_name.ilike.${lnWild})`);
      }

      if (conditions.length === 0) return false;

      const { data: matches, error } = await supabase
        .from('clients_safe')
        .select('id, first_name, last_name, date_of_birth, status, site_id, room_id')
        .is('archived_at', null)
        .or(conditions.join(','))
        .limit(10);

      if (error) {
        console.error('Duplicate check error:', error);
        return false;
      }

      if (!matches?.length) return false;

      // Enrich with room/site info
      const roomIds = matches.filter(m => m.room_id).map(m => m.room_id!);
      const siteIds = matches.filter(m => m.site_id).map(m => m.site_id!);

      const [roomsRes, sitesRes] = await Promise.all([
        roomIds.length > 0
          ? supabase.from('rooms').select('id, room_number').in('id', roomIds)
          : Promise.resolve({ data: [] }),
        siteIds.length > 0
          ? supabase.from('sites_public').select('id, name').in('id', siteIds)
          : Promise.resolve({ data: [] }),
      ]);

      const roomMap = new Map((roomsRes.data || []).map(r => [r.id, r.room_number]));
      const siteMap = new Map((sitesRes.data || []).map(s => [s.id, s.name]));

      const enriched: DuplicateCandidate[] = matches.map(m => {
        const matchReasons: string[] = [];

        if (
          m.first_name?.toLowerCase().includes(firstName.trim().toLowerCase()) &&
          m.last_name?.toLowerCase().includes(lastName.trim().toLowerCase())
        ) {
          matchReasons.push('Name match');
        }

        if (dateOfBirth && m.date_of_birth === dateOfBirth) {
          matchReasons.push('DOB match');
        }

        return {
          id: m.id,
          first_name: m.first_name || '',
          last_name: m.last_name || '',
          date_of_birth: m.date_of_birth,
          status: m.status,
          room_number: m.room_id ? roomMap.get(m.room_id) : undefined,
          site_name: m.site_id ? siteMap.get(m.site_id) : undefined,
          match_reasons: matchReasons.length > 0 ? matchReasons : ['Partial match'],
        };
      });

      setCandidates(enriched);
      setShowDialog(true);
      return true; // duplicates found
    } catch (err) {
      console.error('Duplicate check failed:', err);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  const logOverride = useCallback(async (reason: string, newClientName: string, matchedIds: string[], siteId?: string) => {
    try {
      await logActivity({
        actionType: 'duplicate_override',
        actionDetails: {
          new_client_name: newClientName,
          matched_client_ids: matchedIds,
          override_reason: reason,
        },
        siteId,
      });
    } catch (err) {
      console.error('Failed to log duplicate override:', err);
    }
  }, []);

  const resetCheck = useCallback(() => {
    setCandidates([]);
    setShowDialog(false);
  }, []);

  return {
    candidates,
    isChecking,
    showDialog,
    setShowDialog,
    checkForDuplicates,
    logOverride,
    resetCheck,
  };
};
